import React, { memo, useEffect, useState, useCallback, ChangeEvent, KeyboardEvent } from 'react';
import { Input, IconButton } from '@one-thd/sui-atomic-components';
import { Search, Close } from '@one-thd/sui-icons';

export interface SearchInputProps {
  value?: string;
  onSubmit: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
}

const SearchInputComponent: React.FC<SearchInputProps> = memo(
  ({ value = '', onSubmit, placeholder = 'Search', ariaLabel = 'Search' }: SearchInputProps) => {
    useEffect(() => {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('search-input.ready');
    }, []);

    const [draft, setDraft] = useState(value);

    useEffect(() => {
      setDraft(value);
    }, [value]);

    const submit = useCallback(() => {
      onSubmit(draft.trim());
    }, [draft, onSubmit]);

    const handleClear = useCallback(() => {
      setDraft('');
      // A search is only "active" once it's been submitted (reflected back via `value`). If nothing
      // has been submitted yet, clearing is just a local text-field reset — there's no applied filter
      // to remove, so we don't call onSubmit.
      if (value) {
        onSubmit('');
      }
    }, [value, onSubmit]);

    return (
      // Input doesn't expose a size/className override for its internal root height or padding,
      // so pin it to the same sui-h-9 used by the other toolbar controls (DateRangeFilter,
      // FilterTriggerButton, ColumnCustomizationMenu) via a descendant override on the wrapper —
      // the default sui-py-3 input padding would otherwise make this control taller than the rest.
      <div className="[&_.sui-input-base-root]:sui-h-9 [&_.sui-input-base-input]:sui-py-2">
        <Input
          value={draft}
          placeholder={placeholder}
          fullWidth
          onChange={(event: ChangeEvent<HTMLInputElement>) => setDraft(event.target.value)}
          inputProps={{
            'aria-label': ariaLabel,
            onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') {
                submit();
              }
            }
          }}
          endAdornment={
            <div className="sui-flex sui-items-center sui-gap-1">
              {draft.length > 0 && <IconButton aria-label="Clear search" icon={Close} onClick={handleClear} />}
              <IconButton aria-label="Submit search" icon={Search} color="brand" onClick={submit} />
            </div>
          }
        />
      </div>
    );
  }
);

SearchInputComponent.displayName = 'SearchInput';

const SearchInput = SearchInputComponent;

export { SearchInput };
